import React, { useMemo, useState } from 'react';
import Wrapper from 'components/layouts/Wrapper/Wrapper';
import { Headers, LoadingSpinner } from 'components/core';
import { Button } from 'components/core/Button/Button';
import ListEmpty from '../../Common/ListEmpty/ListEmpty';
import { useGetBuyerCheckAgingQuery } from 'services/reportsApi';
import CheckAgingStats from './CheckAgingStats';
import PropertyFilter from './PropertyFilter';
import VendorPivotTable from './VendorPivotTable';
import { groupByVendor, summarizeBuckets, uniqueProperties } from './pivot';
import { csvFilename, toCsv, triggerCsvDownload } from './csvExport';

export const CheckAgingContainer: React.FC = () => {
  const { data, isLoading, isSuccess } = useGetBuyerCheckAgingQuery();
  const [propertyId, setPropertyId] = useState<string | null>(null);

  const rows = data?.rows ?? [];
  const properties = useMemo(() => uniqueProperties(rows), [rows]);
  const filteredRows = useMemo(
    () => (propertyId ? rows.filter((r) => r.property_id === propertyId) : rows),
    [rows, propertyId]
  );
  const summary = useMemo(() => summarizeBuckets(filteredRows), [filteredRows]);
  const groups = useMemo(() => groupByVendor(filteredRows), [filteredRows]);

  const handleExport = () => {
    if (filteredRows.length === 0) return;
    triggerCsvDownload(toCsv(filteredRows), csvFilename());
  };

  return (
    <Wrapper additionalClasses="flex-col items-start" pageTitle="Check Aging Report">
      {isLoading && <LoadingSpinner />}
      <Headers headingContent="Check Aging Report" />
      {isSuccess && rows.length === 0 && <ListEmpty subText="No outstanding buyer checks." />}
      {isSuccess && rows.length > 0 && (
        <>
          <div className="flex w-full items-center justify-between">
            <PropertyFilter properties={properties} value={propertyId} onChange={setPropertyId} />
            <Button
              variants="extraSmall"
              buttonLabel="Export CSV"
              isDisabled={filteredRows.length === 0}
              onClick={handleExport}
              additionalClasses="h-9 px-4"
            />
          </div>
          <CheckAgingStats summary={summary} />
          {filteredRows.length === 0 ? (
            <ListEmpty subText="No outstanding checks for this property." />
          ) : (
            <VendorPivotTable groups={groups} />
          )}
        </>
      )}
    </Wrapper>
  );
};

export default CheckAgingContainer;
